import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import InlineTable from '../primitives/InlineTable';
import { buildBreadcrumb } from '../lib/breadcrumbFormat';
import { isPinnedHashtagsInput, selectsChartView } from '../lib/listInputs';
import {
    type ColumnSpec,
    buildColumns,
    buildRows,
    parentName,
    pinnedTitleParts,
    excludedTerms,
} from '../lib/tableHelpers';
import type { TableModel, VisualProps, VisualWidget } from '../types';

const NAME_KEY = 'hashtag_name';
const TYPE_LABEL = 'Hashtag';

// Keys reference metrics.js so a rename there propagates here (and to parity with
// the explore hashtag table) instead of silently diverging.
const M = TIKTOK_METADATA_BY_KEY;

// Every sortable hashtag column; an order_by outside this set falls back to default.
const SORTABLE_KEYS = new Set([
    M.non_cum_avg_weekly_views.key,
    M.views.key,
    M.non_cum_increase_WoW.key,
    M.non_cum_WoW.key,
    M.non_cum_increase_MoM_rolling.key,
    M.non_cum_MoM_rolling.key,
    M.non_cum_increase_QoQ_rolling.key,
    M.non_cum_QoQ_rolling.key,
    M.non_cum_increase_YoY_rolling.key,
    M.non_cum_YoY_rolling.key,
    M.total_posts_cnt.key,
    M.partner_paid_partnership_posts_views_prc.key,
    M.eligible_for_commission_posts_views_prc.key,
    M.engagement_prc.key,
]);

// Volume = avg weekly views; hashtags have no life_cycle, so growth tone carries
// the "heat". WoW increase is the default sort and the volume column's growth pair.
const COLUMN_SPEC: ColumnSpec = {
    volumeKey: M.non_cum_avg_weekly_views.key,
    defaultSortKey: M.non_cum_increase_WoW.key,
    volumeGrowthFallback: M.non_cum_increase_WoW.key,
    sortableKeys: SORTABLE_KEYS,
    metaByKey: TIKTOK_METADATA_BY_KEY,
};

/**
 * Per-row subtext under the hashtag: "brand · cluster" for branded rows, just
 * the cluster otherwise. Hashtags have no life_cycle, so the cluster fills the
 * subtext slot the pop-index tables use for the life-cycle badge. Values stay
 * raw — the subtext cell casing is CSS (`capitalize` in InlineTable).
 */
function formatHashtagSubtext(
    rawRow: { brand_name?: unknown; cluster?: unknown } | undefined,
    showBrand: boolean,
): string | undefined {
    const parts: string[] = [];
    const brand = rawRow?.brand_name;
    if (showBrand && typeof brand === 'string' && brand.trim()) {
        parts.push(brand.trim());
    }
    const cluster = rawRow?.cluster;
    if (
        typeof cluster === 'string' &&
        cluster.trim() &&
        cluster !== 'no cluster'
    ) {
        parts.push(cluster.trim());
    }
    return parts.length ? parts.join(' · ') : undefined;
}

function buildHashtagsTableModel({
    input,
    output,
    title: customTitle,
}: VisualProps): TableModel {
    // Prefer the backend-resolved sort (a preset sets it server-side, not echoed
    // in the tool input); fall back to an explicit input sort.
    const orderBy = output?.result?.order_by ?? input?.order_by;
    const columns = buildColumns(orderBy, COLUMN_SPEC);
    const branded = !!input?.branded;
    // A pinned branded list is still branded — but it is deduped to one row per
    // hashtag, so the brand on that row is whichever context the dedupe kept.
    // Name the lens in the title, not a single arbitrary brand per row.
    const showRowBrand = branded && !isPinnedHashtagsInput(input);
    return {
        breadcrumb: buildBreadcrumb(output, TYPE_LABEL),
        title: {
            custom: customTitle || undefined,
            parent: parentName(output),
            noun: branded ? 'brand hashtags' : 'hashtags',
            ...pinnedTitleParts(output, NAME_KEY),
            ...excludedTerms(input, output),
            // The applied tactical lens, when a preset was used (no preset → none).
            lens: output?.result?.applied_preset,
        },
        link: output?.result?.dashboard_url,
        nameColumnHeader: 'Hashtag',
        columns,
        // Hashtag names render as secondary badge pills (per Figma).
        nameAsBadge: true,
        preserveNameCase: true,
        rows: buildRows(
            output,
            columns,
            NAME_KEY,
            TIKTOK_METADATA_BY_KEY,
            (raw) => formatHashtagSubtext(raw, showRowBrand),
        ),
    };
}

const HashtagsInlineTable = ({
    Frame,
    dense,
    onExcludeRow,
    ...props
}: VisualProps) => (
    <InlineTable
        {...buildHashtagsTableModel(props)}
        Frame={Frame}
        dense={dense}
        onExcludeRow={onExcludeRow}
    />
);

const widget: VisualWidget = {
    match: (input) =>
        input?.data_source === 'tiktok' &&
        input?.entity_relationship === 'hashtags' &&
        !selectsChartView(input),
    Component: HashtagsInlineTable,
};

export default widget;
